import type { ActiveTimer, Project } from '@timato/shared';
import { formatClock } from '@/lib/time';
import { elapsedSec, isBreak, MODE_LABEL, remainingSec } from '@/timer/engine';
import type { Goal } from '@/timer/goal';

/** A goal as a person reads it: its words, or else its project's name. */
export const goalName = (g: Goal, projects: Project[]) => g.goalText.trim() || projects.find((p) => p.id === g.projectId)?.name || '';

/** The run a start would end, as the Timer shows it: "Tracking “Support case”, 12:34". */
export function describeRun(t: ActiveTimer, projects: Project[], now: Date) {
  const left = remainingSec(t, now);
  const clock = left == null ? formatClock(elapsedSec(t, now)) : `${formatClock(Math.max(0, left))} left`;
  const name = goalName(t, projects);
  const what = isBreak(t.mode) ? `a ${MODE_LABEL[t.mode].toLowerCase()}` : name && `“${name}”`;
  const verb = t.status === 'paused' ? 'Paused on' : isBreak(t.mode) ? 'On' : t.mode === 'track' ? 'Tracking' : 'Focusing on';
  return what ? `${verb} ${what}, ${clock}` : `${verb.replace(/ on$/, '')}, ${clock}`;
}

/**
 * The run going on under a previewed mode (TW-33), without its clock, in the line under the controls and in the
 * question before Start: they say what carries on, and a ticking figure beside a clock showing another mode reads as
 * a second timer. "Still tracking “Support case”".
 */
export function describeLive(t: ActiveTimer, projects: Project[]) {
  const name = goalName(t, projects);
  const quoted = name && `“${name}”`;
  if (isBreak(t.mode)) return `${t.status === 'paused' ? 'Paused' : 'Still'} on a ${MODE_LABEL[t.mode].toLowerCase()}`;
  if (t.status === 'paused') return quoted ? `${quoted} is paused` : `${MODE_LABEL[t.mode]} is paused`;
  if (t.mode === 'track') return quoted ? `Still tracking ${quoted}` : 'Still tracking';
  return quoted ? `Still focusing on ${quoted}` : 'Still focusing';
}
